import type { ApiNode, Cluster, Gpu, Lease, ReviewItem, Team, TeamRole, User, Vm } from '../api/types'
import { isTestArtifact, nodeName, status } from '../domain/node'
import type { Status } from '../domain/node'
import { DASH } from '../ui/format'

// 화면이 실제로 그리는 모양. API 응답을 한 번만 정리해 두고 뷰는 이것만 읽는다.
// `World` 는 관리자 껍데기가 늘 들고 있는 작은 목록들이다 — VM·사람 수에 비례해 커지는 목록(임대·VM·계정)은 싣지 않고,
// 그것을 그리는 화면이 서버에 쪽으로 묻는다(`hooks/usePage.ts`).
// 이 층은 API 의 현재 사실만 옮긴다. 이벤트·도달성에서 조치를 추론하지 않는다.

export type NodeRow = {
  node: ApiNode
  name: string
  status: Status
}

/** 사람 하나와 그 소속. 계정 줄은 소속을 싣지 않으므로 팀 목록의 멤버에서 잇는다(`personRow`). */
export type Person = {
  user: User
  teams: { team: Team; role: TeamRole }[]
}

export type World = {
  clusters: Cluster[]
  nodes: NodeRow[]
  teams: Team[]
  /** 검토 대기 — 서버가 조합한 순서 그대로. 상단 바의 배지와 레일의 첫 화면이 이 수를 본다. */
  review: ReviewItem[]
}

export type Input = {
  clusters: Cluster[]
  nodes: ApiNode[]
  teams: Team[]
  /** 서버가 조합한 검토 대기. 목록 화면 밖의 호출자는 비워 둔다. */
  review?: ReviewItem[]
}

export const EMPTY: Input = { clusters: [], nodes: [], teams: [], review: [] }

export function build(input: Input): World {
  const rows: NodeRow[] = input.nodes
    .filter((n) => !isTestArtifact(n))
    .map((node) => ({
      node,
      name: nodeName(node),
      status: status(node),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

  return {
    // 제거된 클러스터는 기록일 뿐 자원이 아니다. 어느 화면에도 나오지 않는다.
    clusters: input.clusters.filter((c) => c.state !== 'removed').sort((a, b) => a.name.localeCompare(b.name)),
    nodes: rows,
    teams: [...input.teams].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    review: input.review ?? [],
  }
}

export function membersOf(world: World, clusterId: string): NodeRow[] {
  return world.nodes.filter((r) => r.node.cluster_id === clusterId)
}

/**
 * 서버에서 읽은 계정 줄 하나를 사람의 줄로 — 계정 줄은 소속을 싣지 않으므로 `World` 의 팀 목록의 멤버에서 그 사람이 든 팀과 그 자리를 잇는다.
 * 사람의 표(`PeopleTable`)와 고르는 창이 읽는 것은 이것이다.
 */
export function personRow(user: User, teams: Team[]): Person {
  return {
    user,
    teams: teams.flatMap((team) => {
      const m = team.members.find((x) => x.user_id === user.id)
      return m ? [{ team, role: m.role }] : []
    }),
  }
}

/**
 * 그 사람의 팀 칸(F-42): 관리자는 자기가 관리하는 팀(소유자인 팀), 사용자는 속한 팀이다.
 * 관리자가 학생으로 들어가 있을 뿐인 팀은 관리하는 팀이 아니다.
 */
export function teamsOf(p: Person): Team[] {
  const mine = p.user.role === 'user' ? p.teams : p.teams.filter((t) => t.role === 'owner')
  return mine.map((t) => t.team)
}

/**
 * 그 사람의 상세를 여는가. 관리자의 사용자 목록에는 모든 사람이 서지만 VM·임대·쿼터는 자기가 소유자인 팀의
 * 사람과 자기 것만 온다 — 그 밖의 사람의 상세를 열면 `VM 없음` 처럼 없는 사실을 말한다.
 * 백엔드가 그 읽기들을 여는 술어(소유자인 팀의 멤버)와 같다.
 */
export function opensDetail(world: World, me: Pick<User, 'id' | 'role'>, userId: string): boolean {
  if (me.role === 'superadmin' || userId === me.id) return true
  return world.teams.some((t) => t.my_role === 'owner' && t.members.some((m) => m.user_id === userId))
}

/** VM 목록의 한 줄: 사용자 · VM · 상태 · 접속. 임대가 축이고 VM 은 그 임대가 받은 기계다. */
export type LeaseRow = {
  lease: Lease
  /** 임대 응답에 실린 산 게스트. 아직 없거나 묘비·오류면 null. */
  vm: Vm | null
}

/**
 * 클러스터 상태를 화면의 말로. `founding` 은 지금 만들어지는 중이라 점검과 다른 것이다 —
 * 하나로 뭉개면 창설 중인 클러스터가 고장난 것처럼 보인다. active 라도 답하는 멤버가 없으면
 * 사용 가능이 아니다: 멤버가 있으면 응답 없음, 하나도 없으면 비어 있음이다.
 */
export function clusterStatus(c: Cluster): string {
  switch (c.state) {
    case 'active':
      if (c.live) return '사용 가능'
      return c.member_count === 0 ? '비어 있음' : '응답 없음'
    case 'founding':
      return '창설 중'
    case 'draining':
    case 'maintenance':
      return '점검 중'
    case 'removed':
      return '제거됨'
    default:
      return DASH
  }
}

/** 클러스터 상태의 색. 답하는 멤버가 없는 것만 위험이고, 점검은 경고, 만들어지는 중·비어 있음은 색이 없다. */
export function clusterTone(c: Cluster): 'ok' | 'warn' | 'danger' | 'none' {
  if (c.state === 'active') return c.live ? 'ok' : c.member_count === 0 ? 'none' : 'danger'
  if (c.state === 'draining' || c.state === 'maintenance') return 'warn'
  return 'none'
}

/**
 * 클러스터 카드 아이콘의 색 — 노드들의 상태를 하나로 접는다.
 * 답하는 멤버가 없으면 빨강, 점검 중이거나 정상이 아닌 노드가 하나라도 있으면 노랑, 창설 중·비어 있음·준비 중인 노드가 있으면 파랑, 모두 정상이면 초록.
 * 준비 중은 비정상이 아니다 — 준비 중 하나와 정상 넷은 파랑이고, 오프라인 하나와 정상 넷은 노랑이다.
 */
export function clusterIconTone(c: Cluster, statuses: Status[]): 'ok' | 'warn' | 'danger' | 'preparing' {
  const tone = clusterTone(c)
  if (tone === 'danger') return 'danger'
  if (tone === 'warn' || statuses.some((s) => s !== 'ready' && s !== 'preparing')) return 'warn'
  if (c.state === 'founding' || statuses.length === 0 || statuses.some((s) => s === 'preparing')) return 'preparing'
  return 'ok'
}

/** 상단 바의 검색. 이름만 본다 — 사람이 아는 것은 이름뿐이다. */
export function match(name: string | null | undefined, query: string): boolean {
  const q = query.trim().toLowerCase()
  return q === '' || (name ?? '').toLowerCase().includes(q)
}

export function matchAny(names: (string | null | undefined)[], query: string): boolean {
  return names.some((n) => match(n, query))
}

// ─── 클러스터별 자원 ──────────────────────────────────────────────────────────
//
// 클러스터 카드의 자원. 네 자원(CPU · 메모리 · 스토리지 · GPU) × 세 값(총량 · 할당 · 사용량).
// 총량과 사용량은 PVE 실측(`nodes[].resources`), 할당은 우리 원장(노드가 싣는 `allocated` 와 GPU 행)이다.
// 값이 없으면 `null` 이고 화면은 그 자리에 대시를 찍는다 — 0 으로 그리지 않는다.

export type Amount = { total: number | null; allocated: number; used: number | null }

export type ClusterResources = {
  cluster: Cluster
  nodes: number
  /**
   * 멤버가 전부 읽혔고(목록의 멤버 수 = 클러스터의 member_count) 하나도 빠짐없이 실측이 있을 때만 true.
   * 총량·사용량은 이것과 무관하게 응답하는 멤버의 합이다 — 지금 쓸 수 있는 자원. 어느 멤버도 값을 주지 않은 자원만 null.
   */
  measured: boolean
  /** 단위: 코어. 사용량은 threads × usage 의 합. */
  cpu: Amount
  /** 단위: MB. */
  memory: Amount
  /** 단위: MB. 공유 스토리지는 클러스터 안에서 이름별 한 번, 로컬은 노드별로 더한다. 할당은 멤버의 `allocated.disk_gb` 합(GB → MB) — 루트와 데이터 볼륨. */
  storage: Amount
  /** 단위: 장. 총량·할당은 원장, 사용량은 늘 null. */
  gpu: Amount
}

export function clusterResources(world: World, c: Cluster): ClusterResources {
  const members = membersOf(world, c.id)
  const measured =
    members.length > 0 && members.length === c.member_count && members.every((r) => r.node.resources.reachable)

  // 지금 쓸 수 있는 자원 — 응답하는 멤버의 실측 합이다. 응답하지 않는 멤버의 용량은 지금 쓸 수 없으니 총량에도 없고,
  // 사용할 수 없는 스토리지도 세지 않는다. 어느 멤버도 값을 주지 않은 자원만 null 이라 클러스터가 이상해도 남은 자원은 숫자다.
  let threads = 0
  let cpuUsed = 0
  let cpuAny = false
  let memTotal = 0
  let memUsed = 0
  let memAny = false
  let stTotal = 0
  let stUsed = 0
  let stAny = false
  // 공유 스토리지는 클러스터 안에서 이름별 한 번이다 — 멤버를 돌며 같은 집합을 쓴다.
  const shared = new Set<string>()
  for (const r of members) {
    const res = r.node.resources
    if (!res.reachable) continue
    if (res.cpu) {
      cpuAny = true
      threads += res.cpu.threads
      cpuUsed += res.cpu.threads * res.cpu.usage
    }
    if (res.memory) {
      memAny = true
      memTotal += res.memory.total_mb
      memUsed += res.memory.used_mb
    }
    const st = sumStorages(res.storages, shared)
    if (st.counted) {
      stAny = true
      stTotal += st.total
      stUsed += st.used
    }
  }

  // 할당은 서버의 노드 원장(`allocated`)이고 클러스터는 멤버의 합이다 — VM 을 모아 세지 않는다.
  const alloc = members.reduce(
    (a, r) => ({ cores: a.cores + r.node.allocated.cores, mem: a.mem + r.node.allocated.memory_mb, disk: a.disk + r.node.allocated.disk_gb }),
    { cores: 0, mem: 0, disk: 0 },
  )
  const cards = members.flatMap((r) => r.node.gpus)

  return {
    cluster: c,
    nodes: members.length,
    measured,
    cpu: { total: cpuAny ? threads : null, allocated: alloc.cores, used: cpuAny ? cpuUsed : null },
    memory: { total: memAny ? memTotal : null, allocated: alloc.mem, used: memAny ? memUsed : null },
    storage: {
      total: stAny ? stTotal : null,
      allocated: alloc.disk * 1024,
      used: stAny ? stUsed : null,
    },
    gpu: gpuAmount(cards),
  }
}

/** 스토리지 합(MB). 사용할 수 없는 스토리지는 세지 않고, `seenShared` 에 이미 있는 공유 스토리지는 건너뛰고 집합에 더한다. `counted` 는 하나라도 셌는지다. */
function sumStorages(storages: ApiNode['resources']['storages'], seenShared: Set<string>): { total: number; used: number; counted: boolean } {
  let total = 0
  let used = 0
  let counted = false
  for (const s of storages) {
    if (s.status !== 'available') continue
    if (s.shared) {
      if (seenShared.has(s.name)) continue
      seenShared.add(s.name)
    }
    counted = true
    total += s.total_mb
    used += s.used_mb
  }
  return { total, used, counted }
}

/** GPU 는 원장이다: 총량은 카드 수, 할당은 `allocated` 카드 수, 사용량은 늘 없다. */
function gpuAmount(cards: Gpu[]): Amount {
  return { total: cards.length, allocated: cards.filter((g) => g.state === 'allocated').length, used: null }
}

// ─── 노드 자원과 배치 ────────────────────────────────────────────────────────
//
// 클러스터 화면의 계층: 클러스터 카드 → 노드 카드. 노드 막대는 그 노드의 실측과 그 노드가 싣는 할당(`allocated`)이다.
// VM 은 여기서 읽지 않는다 — 노드의 할당이 VM 의 전부이고, 대수는 노드가 싣는 `vm_count` 다.

export type NodeResources = {
  row: NodeRow
  /** `resources.reachable`. false 면 CPU·메모리·스토리지의 총량·사용량은 null 이고 할당은 남는다. */
  measured: boolean
  /** 단위: 코어. 사용량은 threads × usage. */
  cpu: Amount
  /** 단위: MB. */
  memory: Amount
  /** 단위: MB. 이 노드의 사용할 수 있는 스토리지 합 — 사용할 수 있는 것이 하나도 없으면 총량·사용량은 null. 할당은 `allocated.disk_gb`(GB → MB). */
  storage: Amount
  /** 단위: 장. 원장이고 사용량은 늘 null. */
  gpu: Amount
}

export function nodeResources(row: NodeRow): NodeResources {
  const res = row.node.resources
  const measured = res.reachable
  const cpu = measured ? res.cpu : undefined
  const memory = measured ? res.memory : undefined
  // 스토리지는 사용할 수 있는 것만 센다 — 하나가 죽어도 남은 것은 지금 쓸 수 있는 자원이다.
  const st = sumStorages(res.storages, new Set())
  const storageOk = measured && st.counted
  const alloc = row.node.allocated
  return {
    row,
    measured,
    cpu: { total: cpu ? cpu.threads : null, allocated: alloc.cores, used: cpu ? cpu.threads * cpu.usage : null },
    memory: { total: memory ? memory.total_mb : null, allocated: alloc.memory_mb, used: memory ? memory.used_mb : null },
    storage: { total: storageOk ? st.total : null, allocated: alloc.disk_gb * 1024, used: storageOk ? st.used : null },
    gpu: gpuAmount(row.node.gpus),
  }
}

export type NodePlacement = { node: NodeResources }
export type ClusterPlacement = { cluster: Cluster; resources: ClusterResources; nodes: NodePlacement[] }
export type Placement = {
  clusters: ClusterPlacement[]
  /** 클러스터가 없거나 목록에 없는 클러스터의 노드. 배치 대상이 아니다. */
  unclustered: NodePlacement[]
}

export function placement(world: World): Placement {
  const nodePlacement = (row: NodeRow): NodePlacement => ({ node: nodeResources(row) })
  const clusterIds = new Set(world.clusters.map((c) => c.id))
  return {
    clusters: world.clusters.map((c) => ({
      cluster: c,
      resources: clusterResources(world, c),
      nodes: membersOf(world, c.id).map(nodePlacement),
    })),
    unclustered: world.nodes.filter((r) => !r.node.cluster_id || !clusterIds.has(r.node.cluster_id)).map(nodePlacement),
  }
}
